import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiDownload, FiRefreshCw, FiSearch, FiFilter, FiCheck, FiX } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalDeposits: 0,
    completedPayments: 0,
    pendingPayments: 0
  });

  useEffect(() => {
    loadPayments();
  }, [filter]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('payments')
        .select(`
          *,
          vehicles (
            year,
            make,
            model,
            vin
          )
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setPayments(data || []);
      calculateStats(data || []);
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (paymentData) => {
    const stats = {
      totalRevenue: 0,
      totalDeposits: 0,
      completedPayments: 0,
      pendingPayments: 0
    };

    paymentData.forEach(payment => {
      if (payment.status === 'completed') {
        stats.totalRevenue += parseFloat(payment.amount);
        stats.completedPayments++;
        if (payment.type === 'deposit') {
          stats.totalDeposits++;
        }
      } else if (payment.status === 'pending') {
        stats.pendingPayments++;
      }
    });

    setStats(stats);
  };

  const updatePaymentStatus = async (paymentId, newStatus) => {
    try {
      // Get the payment details first
      const { data: payment, error: fetchError } = await supabase
        .from('payments')
        .select('*')
        .eq('id', paymentId)
        .single();
        
      if (fetchError) throw fetchError;
      
      // If approving (marking as completed), we need to capture the payment
      if (newStatus === 'completed' && payment.status === 'pending') {
        console.log('Capturing payment in Authorize.net:', payment);
        
        // For now, just update the database
        // TODO: Add actual capture logic through Authorize.net API
        console.warn('⚠️ Payment capture not implemented - payment was authorized but not captured!');
        console.warn('You need to manually capture this in Authorize.net dashboard');
        console.warn('Transaction ID:', payment.processor_transaction_id);
      }
      
      // Update database status
      const { error } = await supabase
        .from('payments')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) throw error;

      alert(newStatus === 'completed' 
        ? '✅ Payment marked as completed (Note: You may need to manually capture in Authorize.net)' 
        : `Payment status updated to ${newStatus}`);
      
      loadPayments();
    } catch (err) {
      console.error('Error updating payment:', err);
      alert('Failed to update payment status');
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment.customer_name?.toLowerCase().includes(search) ||
      payment.customer_email?.toLowerCase().includes(search) ||
      payment.stripe_payment_intent_id?.toLowerCase().includes(search) ||
      payment.vehicles?.vin?.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'deposit': return 'Deposit';
      case 'full_payment': return 'Full Payment';
      case 'partial_payment': return 'Partial Payment';
      default: return type;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi mb-2">Payment Management</h2>
        <p className="text-gray-600">Track and manage all customer payments</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">
                ${stats.totalRevenue.toLocaleString()}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">{stats.completedPayments}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <SafeIcon icon={FiCheck} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Deposits</p>
              <p className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">{stats.totalDeposits}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <SafeIcon icon={FiDownload} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">{stats.pendingPayments}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <SafeIcon icon={FiRefreshCw} className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by customer, email, or transaction ID..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-adm-ctl text-[13px] font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-3 py-1.5 rounded-adm-ctl text-[13px] font-medium transition-colors ${
                filter === 'completed' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 py-1.5 rounded-adm-ctl text-[13px] font-medium transition-colors ${
                filter === 'pending' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('failed')}
              className={`px-3 py-1.5 rounded-adm-ctl text-[13px] font-medium transition-colors ${
                filter === 'failed' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Failed
            </button>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No payments found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>
                    Date
                  </th>
                  <th>
                    Customer
                  </th>
                  <th>
                    Vehicle
                  </th>
                  <th>
                    Type
                  </th>
                  <th>
                    Amount
                  </th>
                  <th>
                    Status
                  </th>
                  <th>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {payment.customer_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {payment.customer_email}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      {payment.vehicles ? (
                        <div>
                          {payment.vehicles.year} {payment.vehicles.make} {payment.vehicles.model}
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {getTypeLabel(payment.type)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap font-medium">
                      ${parseFloat(payment.amount).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      {payment.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => updatePaymentStatus(payment.id, 'completed')}
                            className="text-green-600 hover:text-green-700"
                            title="Mark as Completed"
                          >
                            <SafeIcon icon={FiCheck} className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => updatePaymentStatus(payment.id, 'failed')}
                            className="text-red-600 hover:text-red-700"
                            title="Mark as Failed"
                          >
                            <SafeIcon icon={FiX} className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {payment.status === 'completed' && (
                        <button
                          onClick={() => updatePaymentStatus(payment.id, 'refunded')}
                          className="text-gray-600 hover:text-gray-700"
                          title="Refund"
                        >
                          <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentManagement;
